import store from '../state/store.js'
import {
	FORGOTTEN_PASSWORD,
	SESSION_EXPIRED,
	DATA_SUBJECT_SEARCH,
	RESET_PASSWORD,
	CREATE_DATA_SUBJECT,
	DATA_SUBJECT,
	LOGIN,
	FORBIDDEN,
	UNAUTHORISED,
	NOT_FOUND,
	SSO_ERROR,
	LOGOUT,
	DIRECT_LOGIN_NOT_ALLOWED,
	ACCOUNT_LOCKED_OUT,
	SEARCH,
	MULTI_SEARCH,
	NO_ACCESS
} from './route-names.js'
import {
	USER_MANAGEMENT,
	USER_MANAGEMENT_CREATE_USER,
	USER_MANAGEMENT_EDIT_USER,
	CS_PORTAL_PRODUCT_ROLES,
	CS_PORTAL_PRODUCT_ROLES_CREATE_ROLE,
	CS_PORTAL_PRODUCT_ROLES_EDIT_ROLE
}
from '../../../shared/router/route-names.js'
import {
	SEE_DATA_SUBJECT_MANAGEMENT_AREA,
	USE_CREATE_DATA_SUBJECTS_AREA,
	CREATE_UPDATE_CSPORTAL_USERS,
	SEE_MANAGE_CSPORTAL_USERS_AREA
} from '../../../shared/permissions/cs-portal-permissions.js'
import { canCreateDataSubjects, canCreateCsPortalUsers, waitForLoad } from '../../../shared/state/configuration.js'
import { VALID_SEARCH_FIELDS } from '../../../shared/lib/magic-link-valid-fields.js'

export default [
	{ path: '/', redirect: { name: DATA_SUBJECT_SEARCH } },
	{
		path: '/create-data-subject',
		name: CREATE_DATA_SUBJECT,
		component: () => import('../components/views/data-subjects/_data-subject-create.vue'),
		meta: {
			authRequired: true,
			permissionRequired: USE_CREATE_DATA_SUBJECTS_AREA
		},
		async beforeEnter (to, from, next) {
			if (typeof canCreateDataSubjects.value === 'undefined') {
				await waitForLoad()
			}

			if (canCreateDataSubjects.value === false) {
				next({ path: '/401' })
			} else if (canCreateDataSubjects.value === true) {
				next()
			}
		}
	},
	{
		path: '/data-subject-search',
		name: DATA_SUBJECT_SEARCH,
		component: () => import('../components/views/data-subjects/_data-subject-search.vue'),
		meta: {
			authRequired: true,
			permissionRequired: SEE_DATA_SUBJECT_MANAGEMENT_AREA
		}
	},
	{
		path: '/data-subject',
		name: DATA_SUBJECT,
		component: () => import('../components/views/data-subjects/data-subject/_data-subject.vue'),
		meta: {
			authRequired: true,
			permissionRequired: SEE_DATA_SUBJECT_MANAGEMENT_AREA
		},
		props: route => ({
			id: parseInt(route.query.id),
			brandId: parseInt(route.query.brandId),
			allIds: route.query.allIds ? route.query.allIds.split(',').map(id => Number(id)) : []
		})
	},
	// Multi-parameter search route - must be defined BEFORE /search/:field/:value
	// Supports: /search?urn1=value&custom3=butler&brandId=1
	// Ticket: #23707
	{
		path: '/search',
		name: MULTI_SEARCH,
		component: () => import('../components/views/data-subjects/multi-data-subject-search.vue'),
		meta: {
			authRequired: true,
			permissionRequired: SEE_DATA_SUBJECT_MANAGEMENT_AREA
		},
		beforeEnter (to, from, next) {
			const redirect = () => next({ name: DATA_SUBJECT_SEARCH })
			const brandId = getBrandIdFromQuery(to.query)
			// Validate brandId format (must be numeric)
			if (!brandId || !/^\d+$/.test(brandId)) return redirect()
			next()
		},
		props: route => ({
			searchParams: extractSearchParams(route.query),
			brandId: getBrandIdFromQuery(route.query)
		})
	},
	// Single-parameter search route without value - redirect to search page
	// Catches: /search/email1, /search/urn1/, etc.
	// This MUST be defined after /search and before /search/:field/:value
	// Ticket: #23760
	{
		path: '/search/:field',
		redirect: to => {
			// Preserve any query parameters (e.g., brandId)
			return {
				name: DATA_SUBJECT_SEARCH,
				query: to.query
			}
		}
	},
	// Single-parameter search route (legacy format)
	// Supports: /search/email1/test@example.com?brandId=1
	// Fix #23484: Redirect on missing value instead of 404
	{
		path: '/search/:field/:value',
		name: SEARCH,
		component: () => import('../components/views/data-subjects/single-data-subject-search.vue'),
		meta: {
			authRequired: true,
			permissionRequired: SEE_DATA_SUBJECT_MANAGEMENT_AREA
		},
		beforeEnter (to, from, next) {
			const redirect = () => next({ name: DATA_SUBJECT_SEARCH })
			const { field, value } = to.params
			const brandId = getBrandIdFromQuery(to.query)

			// Fix #23484: Redirect if field or value is missing/empty
			if (!field || !value) return redirect()

			const f = field.trim()
			const v = value.trim()
			const b = brandId

			// Redirect if any trimmed value is empty or brandId is invalid
			if (!f || !v || !b || !/^\d+$/.test(b)) return redirect()

			next()
		},
		props: route => {
			return {
				searchField: route.params.field.trim(),
				searchValue: route.params.value.trim(),
				brandId: getBrandIdFromQuery(route.query)
			}
		}
	},
	{
		path: '/forgotten-password',
		name: FORGOTTEN_PASSWORD,
		component: () => import('../components/views/login/_forgotten-password.vue')
	},
	{
		path: '/session-expired',
		name: SESSION_EXPIRED,
		component: () => import('../components/views/session-expired.vue')
	},
	{
		path: '/reset-password',
		name: RESET_PASSWORD,
		component: () => import('../components/views/login/_reset-password.vue'),
		props: route => ({
			hash: route.query.hash
		})
	},
	{
		path: '/product-roles/create-role',
		name: CS_PORTAL_PRODUCT_ROLES_CREATE_ROLE,
		component: () => import('../../../shared/components/product-roles/_product-roles-create-role.vue'),
		meta: {
			authRequired: true,
			permissionRequired: CREATE_UPDATE_CSPORTAL_USERS
		},
		props: route => ({
			productIdToView: Number(route.query.productIdToView),
			loggedInProductId: Number(route.query.loggedInProductId)
		})
	},
	{
		path: '/product-roles/edit-role',
		name: CS_PORTAL_PRODUCT_ROLES_EDIT_ROLE,
		component: () => import('../../../shared/components/product-roles/_product-roles-edit-role.vue'),
		meta: {
			authRequired: true,
			permissionRequired: SEE_MANAGE_CSPORTAL_USERS_AREA
		},
		props: route => ({
			roleId: Number(route.query.roleId),
			productIdToView: Number(route.query.productIdToView),
			loggedInProductId: Number(route.query.loggedInProductId)
		})
	},
	{
		path: '/product-roles',
		name: CS_PORTAL_PRODUCT_ROLES,
		component: () => import('../../../shared/components/product-roles/_product-roles.vue'),
		meta: {
			authRequired: true,
			permissionRequired: SEE_MANAGE_CSPORTAL_USERS_AREA
		},
		props: {
			productIdToView: Number(2),
			loggedInProductId: Number(2)
		}
	},
	{
		path: '/user-management/create-user',
		name: USER_MANAGEMENT_CREATE_USER,
		component: () => import('../../../shared/components/user-management/_user-management-create-user.vue'),
		meta: {
			authRequired: true,
			permissionRequired: CREATE_UPDATE_CSPORTAL_USERS
		},
		async beforeEnter (to, from, next) {
			if (typeof canCreateCsPortalUsers.value === 'undefined') {
				await waitForLoad()
			}

			if (canCreateCsPortalUsers.value === false) {
				next({ path: '/401' })
			} else if (canCreateCsPortalUsers.value === true) {
				next()
			}
		}
	},
	{
		path: '/user-management/edit-user',
		name: USER_MANAGEMENT_EDIT_USER,
		component: () => import('../../../shared/components/user-management/_user-management-edit-user.vue'),
		meta: {
			authRequired: true,
			permissionRequired: SEE_MANAGE_CSPORTAL_USERS_AREA
		},
		props: route => ({
			userId: Number(route.query.userId),
			ssoEnabled: route.query.ssoEnabled === 'true',
			ssoBypassDomains: route.query.ssoBypassDomains,
			scimEnabled: route.query.scimEnabled === 'true',
			ssoConfigurationName: route.query.ssoConfigurationName,
			scimExternalId: route.query.scimExternalId
		})
	},
	{
		path: '/user-management',
		name: USER_MANAGEMENT,
		component: () => import('../../../shared/components/user-management/_user-management.vue'),
		meta: {
			authRequired: true,
			permissionRequired: SEE_MANAGE_CSPORTAL_USERS_AREA
		}
	},
	{
		path: '/login',
		name: LOGIN,
		component: () => import('../components/views/login/_login.vue'),
		meta: {
			beforeResolve (routeTo, routeFrom, next) {
				// If the user is already logged in
				if (store.getters['auth/loggedIn']) {
					// Redirect to the home page instead
					next({ name: DATA_SUBJECT_SEARCH })
				} else {
					// Continue to the login page
					next()
				}
			}
		}
	},
	{
		path: '/otp-login',
		name: 'otp-login',
		component: () => import('../components/views/login/_otp-login.vue'),
		props: route => ({
			otp: route.query.otp
		})
	},
	{
		path: '/404',
		name: NOT_FOUND,
		component: () => import('../components/views/_404.vue'),
		// Allows props to be passed to the 404 page through route
		// params, such as `resource` to define what wasn't found.
		props: true
	},
	{
		path: '/403',
		name: FORBIDDEN,
		component: () => import('../components/views/_403.vue'),
		// Allows props to be passed to the 403 page through route
		// params, such as `resource` to define what wasn't found.
		props: true
	},
	{
		path: '/401',
		name: UNAUTHORISED,
		component: () => import('../components/views/_401.vue'),
		// Allows props to be passed to the 403 page through route
		// params, such as `resource` to define what wasn't found.
		props: true
	},
	{
		path: '/sso/error',
		name: SSO_ERROR,
		component: () => import('../../../cs-portal/src/components/views/_sso-error.vue'),
		// Allows props to be passed to the 403 page through route
		// params, such as `resource` to define what wasn't found.
		props: true
	},
	{
		path: '/sso/logout',
		name: LOGOUT,
		component: () => import('../../../cs-portal/src/components/views/_sso-logout.vue'),
		// Displays message to user that logout has occurred
		props: true
	},
	{
		path: '/no-access',
		name: NO_ACCESS,
		component: () => import('../components/views/login/_no-access.vue')
	},
	// Redirect any unmatched routes to the 404 page. This may
	// require some server configuration to work in production:
	// https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
	{
		path: '*',
		redirect: '404'
	},
	// SSO Direct Login
	{
		path: '/direct-login-not-allowed',
		name: DIRECT_LOGIN_NOT_ALLOWED,
		component: () => import('../../../shared/components/direct-login-not-allowed.vue'),
		props: true
	},
	{
		path: '/account-locked-out',
		name: ACCOUNT_LOCKED_OUT,
		component: () => import('../components/views/login/_account-locked-out.vue')
	}
]

const getBrandIdFromQuery = query => {
	const brandIdKey = Object.keys(query).find(k => k.toLowerCase() === 'brandid')
	return brandIdKey ? String(query[brandIdKey]).trim() : '0'
}

/**
 * Checks if query parameters contain duplicate search field names.
 * Detects both exact duplicates (urn1=a&urn1=b) and case-insensitive duplicates (URN1=a&urn1=b).
 * Only validates fields in VALID_SEARCH_FIELDS; invalid fields are ignored.
 *
 * @param {Object} query - Vue Router query object (key-value pairs, values may be strings or arrays)
 * @returns {boolean} True if any valid search field appears multiple times (triggers redirect)
 * @example
 * hasDuplicateFields({ urn1: ['a', 'b'], brandId: '123' })  // true (exact duplicate)
 * hasDuplicateFields({ URN1: 'a', urn1: 'b' })              // true (case variation)
 * hasDuplicateFields({ urn1: 'a', email1: 'b' })            // false (unique fields)
 * Ticket: #23707
 */
const hasDuplicateFields = query => {
	const queryKeys = Object.keys(query)

	// Safeguard: reject excessively large query objects (potential DoS)
	if (queryKeys.length > 20) {
		return true
	}

	const normalizedKeys = new Set()

	for (const key of queryKeys) {
		const lowerKey = key.toLowerCase()

		// Check brandId for duplicates
		if (lowerKey === 'brandid') {
			if (Array.isArray(query[key])) {
				return true
			}
			continue
		}

		// Only check valid field names for duplicates
		if (!VALID_SEARCH_FIELDS.has(lowerKey)) continue

		// Detect exact duplicates (array) or case variation duplicates (already seen)
		if (Array.isArray(query[key]) || normalizedKeys.has(lowerKey)) {
			return true
		}

		normalizedKeys.add(lowerKey)
	}

	return false
}

/**
 * Extracts and validates search parameters from query string.
 * Filters out the brandId parameter.
 * @param {Object} query - Vue Router query object
 * @returns {Object} Object of with { param0: { key, value }, ... }
 * Ticket: #23707
 */
const extractSearchParams = query => {
	const MAX_PARAM_LENGTH = 500

	// Get a flattened object with lowercased keys from query object, include duplicates as separate entries
	const flattenedParams = {}
	let paramIndex = 0

	Object.keys(query).forEach((key) => {
		const lowerKey = key.toLowerCase()

		// Skip brandId parameter (handled separately)
		if (lowerKey === 'brandid') return

		const value = query[key]

		if (Array.isArray(value)) {
			value.forEach((val) => {
				const trimmedVal = String(val).trim()
				if (trimmedVal.length <= MAX_PARAM_LENGTH) {
					flattenedParams[`param${paramIndex}`] = { key: lowerKey, value: trimmedVal }
					paramIndex++
				}
			})
		} else {
			const trimmedVal = String(value).trim()
			if (trimmedVal.length <= MAX_PARAM_LENGTH) {
				flattenedParams[`param${paramIndex}`] = { key: lowerKey, value: trimmedVal }
				paramIndex++
			}
		}
	})
	return flattenedParams
}
